/* eslint-disable indent */
/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../Components/Inbox/Sidebar';
import MessageSection from '../../Components/Inbox/MessageSection';
import UserInfo from '../../Components/Inbox/UserInfo';
import {
  fetchMessages,
  fetchMessageById,
  sendMessage,
  messageAction,
  fetchConnectionSummary
} from '../../Util/ApiService';
import '../../Util/i18n';
import { connectionStatusCodes } from '../../Util/connectionStatuses';
import { removeEntryFromArray } from '../../Util/data';

const PAGE_SIZE = 25;

const Inbox = ({
  locale = '',
  apiUrl = '',
  connectionId = 0,
  acceptNudge = false,
  rejectNudge = false
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [messagePageNumber, setMessagePageNumber] = useState(1);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoResultsLabel, setShowNoResultsLabel] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(true);
  const [showProfileDetails, setShowProfileDetails] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedProposalTitle, setSelectedProposalTitle] = useState(null);
  const [selectedRecipientImage, setSelectedRecipientImage] = useState(null);
  const [isRead, setReadStatus] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [attachmentCollection, setAttachmentCollection] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [additionalParam, setAdditionalParam] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);
  const [inboxHeight, setInboxHeight] = useState(null);
  const [acceptingNudge, setAccepting] = useState(false);
  const [rejectingNudge, setRejecting] = useState(false);

  const [secondaryConnection, setSecondaryConnection] = useState(null);
  const [secondaryConnectionReadValue, setSecondaryConnectionReadValue] =
    useState(null);

  const { t } = useTranslation();

  // eslint-disable-next-line consistent-return
  const getMessages = async (newPageNumber) => {
    const newMessages = additionalParam
      ? await fetchMessages(`${apiUrl}?${additionalParam}`, {
          pageNumber: newPageNumber,
          pageSize: PAGE_SIZE,
          searchQuery
        })
      : await fetchMessages(apiUrl, {
          pageNumber: newPageNumber,
          pageSize: PAGE_SIZE,
          searchQuery
        });
    // eslint-disable-next-line no-unused-expressions
    newPageNumber === 1
      ? setMessages(newMessages.results)
      : setMessages([...messages, ...newMessages.results]);
    setTotalResults(newMessages.pagination.pageNumber * PAGE_SIZE);

    if (newPageNumber === 1 && !newMessages.results.length) {
      setShowNoResultsLabel(true);
    }

    if (!newMessages.results.length) {
      setLoading(false);
      return setHasMoreMessages(false);
    }

    if (newMessages.pagination.totalResults === 0) {
      setHasMoreMessages(false);
      setLoading(false);
      return setShowNoResultsLabel(true);
    }

    setPageNumber(newPageNumber);

    const initialIdx = 0;
    let thread = newMessages.results[initialIdx];

    if (connectionId) {
      const selectedThread = await fetchConnectionSummary(
        `/connection/${connectionId}/summary.json`
      );
      if (selectedThread.archived) {
        setMessages([]);
        setMessages([...messages, selectedThread]);
        thread = selectedThread;
      } else {
        const isFound = newMessages.results.some((message) => {
          return message.connectionId === connectionId;
        });

        if (isFound) {
          thread = newMessages.results.find((mess) => {
            return mess.connectionId === connectionId;
          });
        } else {
          setMessages([selectedThread, ...messages]);
          thread = selectedThread;
        }
      }
    }

    if (newPageNumber === 1) {
      setSelectedRecipient(
        `${thread.interlocutor.name.firstName} ${thread.interlocutor.name.lastName}`
      );
      setSelectedProposalTitle(thread.proposal.title);
      setSelectedConversationId(thread.connectionId);
    }

    if (thread.status.code === connectionStatusCodes.rejected) {
      setLoading(false);
      return setConversationLoading(false);
    }

    // set the last message as the selected one on first load
    if (thread.messagesApiUri && newPageNumber === 1) {
      // eslint-disable-next-line no-use-before-define
      getMessageById(thread.messagesApiUri, 1);
    } else {
      setLoading(false);
      setConversationLoading(false);
      return setShowNoResultsLabel(false);
    }
    setLoading(false);
    setConversationLoading(false);
    setShowNoResultsLabel(false);
  };

  const getMessageById = async (e, page) => {
    const newMessage = await fetchMessageById(
      `${e}?pageSize=${PAGE_SIZE}&pageNumber=${page}`
    );
    if (!newMessage.results.length) {
      return;
    }
    setSelectedConversation(newMessage);
    setConversationLoading(false);

    const tempAttachmentArray = [];

    if (page !== 1) {
      attachmentCollection.forEach((item) => {
        return tempAttachmentArray.push(item);
      });
    }

    // eslint-disable-next-line array-callback-return
    newMessage.results.map((message) => {
      if (message?.attachments?.length) {
        message.attachments.forEach((attachment) => {
          tempAttachmentArray.push(attachment);
        });
        setAttachmentCollection(tempAttachmentArray);
      }
    });
    setMessagePageNumber(page);
  };

  // infinite loading inside message section
  const fetchPreviousMessages = () => {
    getMessageById(selectedConnection.messagesApiUri, messagePageNumber + 1);
  };

  // infinite loading on sidebar
  const fetchMoreMessages = () => {
    getMessages(pageNumber + 1);
  };

  const toggleProfileDetails = () => {
    setShowProfileDetails((value) => {
      return !value;
    });
  };

  const updateSelection = (
    e,
    recipient,
    proposalTitle,
    recipientImage,
    updatedConnectionId
    // eslint-disable-next-line consistent-return
  ) => {
    setSelectedConversationId(updatedConnectionId);
    if (!e) {
      return setSelectedConversation(null);
    }
    setSelectedRecipient(recipient);
    setSelectedProposalTitle(proposalTitle);
    setSelectedRecipientImage(recipientImage);
    if (e) {
      getMessageById(e, 1);
    }
  };

  const updateMessage = async (message, attachments) => {
    setSending(true);
    const formData = new FormData();
    formData.append('content', message);
    attachments.forEach((attachment) => {
      formData.append('attachments[]', attachment, attachment.name);
    });
    // eslint-disable-next-line no-shadow
    const updatedMessage = messages.find((message) => {
      return message.connectionId === selectedConversationId;
    });
    await sendMessage(`${updatedMessage.postMessageUri}`, formData).then(() => {
      // eslint-disable-next-line no-constant-condition
      if ((selectedConnection.messagesApiUri, 1)) {
        getMessageById(selectedConnection.messagesApiUri, 1);
      }
    });
    setSending(false);
  };

  const changeReadStatus = async (value, uri) => {
    if (
      uri === selectedConnection?.connectionUserActions?.markAsNotReadUri ||
      uri === selectedConnection?.connectionUserActions?.markAsReadUri
    ) {
      setReadStatus(value);
    } else {
      const updatedConnection = messages.find((message) => {
        return (
          message?.connectionUserActions?.markAsNotReadUri === uri ||
          message?.connectionUserActions?.markAsReadUri === uri
        );
      });
      updatedConnection.read = !updatedConnection.read;
      setSecondaryConnection(updatedConnection);
      setSecondaryConnectionReadValue(updatedConnection?.read);
    }
    await messageAction(uri);
    setHasMoreMessages(true);
  };

  const updateReadStatus = async (val) => {
    setReadStatus(val);
    const updatedMessage = messages.find((message) => {
      return message.connectionId === selectedConversationId;
    });
    if (val) {
      await messageAction(updatedMessage.connectionUserActions.markAsReadUri);
    } else {
      await messageAction(
        updatedMessage.connectionUserActions.markAsNotReadUri
      );
    }
    setHasMoreMessages(true);
  };

  const updateArchiveStatus = async (uri) => {
    const updatedConnection = messages.find((connection) => {
      return (
        connection?.connectionUserActions?.markAsArchivedUri === uri ||
        connection?.connectionUserActions?.markAsNotArchivedUri === uri
      );
    });
    await messageAction(uri).then(() => {
      return removeEntryFromArray(
        messages,
        messages.indexOf(updatedConnection)
      );
    });
    if (messages.length) {
      const firstMessage = messages[0];
      updateSelection(
        firstMessage?.messagesApiUri,
        `${firstMessage?.interlocutor?.name?.firstName} ${firstMessage?.interlocutor?.name?.lastName}`,
        firstMessage?.proposal?.title,
        firstMessage?.interlocutor?.logoUri,
        firstMessage?.connectionId
      );
    } else {
      setShowNoResultsLabel(true);
    }
  };

  const archiveConnection = (uri) => {
    updateArchiveStatus(uri);
  };

  const restoreConnection = (uri) => {
    updateArchiveStatus(uri);
  };

  // accept or reject connection
  const connectionAction = async (uri) => {
    // eslint-disable-next-line no-unused-expressions
    uri.includes(true) ? setAccepting(true) : setRejecting(true);
    await messageAction(uri).then(() => {
      if (uri.includes(true)) {
        getMessageById(selectedConnection.messagesApiUri, 1);
        selectedConnection.status.code = connectionStatusCodes.new;
      } else {
        removeEntryFromArray(messages, messages.indexOf(selectedConnection));
        const firstMessage = messages[0];
        updateSelection(
          firstMessage?.messagesApiUri,
          `${firstMessage?.interlocutor?.name?.firstName} ${firstMessage?.interlocutor?.name?.lastName}`,
          firstMessage?.proposal?.title,
          firstMessage?.interlocutor?.logoUri,
          firstMessage?.connectionId
        );
      }
    });
    setHasMoreMessages(true);
    setAccepting(false);
    setRejecting(false);
  };

  // inbox filters
  const updateFilter = (e) => {
    switch (e) {
      case 'all':
        setAdditionalParam(null);
        break;
      case 'deleted':
        setAdditionalParam('archivedOnly=true');
        break;
      case 'read':
        setAdditionalParam('read=true');
        break;
      case 'unread':
        setAdditionalParam('read=false');
        break;
      default:
        setAdditionalParam(null);
    }
  };

  // filter messages by search query
  const filterByQuery = (query) => {
    setSearchQuery(query);
  };

  useEffect(() => {
    setLoading(true);
    setHasMoreMessages(true);
    getMessages(1);
  }, [additionalParam, searchQuery]);

  useEffect(() => {
    const currentMessage = messages.find((message) => {
      return message.connectionId === selectedConversationId;
    });
    setSelectedConnection(currentMessage);
    setAttachmentCollection([]);
    setSelectedConversation(null);
  }, [selectedConversationId]);

  useEffect(() => {
    setReadStatus(true);
    if (
      selectedConnection?.status.code ===
      connectionStatusCodes?.pendingAcceptance
    ) {
      if (acceptNudge) {
        connectionAction(
          selectedConnection.connectionUserActions.acceptConnectionUri
        );
      }
      if (rejectNudge) {
        connectionAction(
          selectedConnection.connectionUserActions.rejectConnectionUri
        );
      }
    }
  }, [selectedConnection]);

  useEffect(() => {
    const updateInboxHeight = () => {
      const headerHeight = document.querySelector('header').offsetHeight;
      setInboxHeight(window.innerHeight - headerHeight);
    };

    window.addEventListener('resize', updateInboxHeight);
    updateInboxHeight();
    return () => {
      return window.removeEventListener('resize', updateInboxHeight);
    };
  }, []);

  return (
    <div
      className="w-full flex lg:flex-wrap overflow-auto"
      style={{ height: `${inboxHeight}px` }}
    >
      <Sidebar
        loading={loading}
        conversations={messages}
        updateSelectedConversation={updateSelection}
        activeId={selectedConversationId || ''}
        secondaryConnection={secondaryConnection || null}
        secondaryConnectionReadValue={secondaryConnectionReadValue || null}
        isRead={isRead}
        totalMessages={totalResults}
        fetchMoreMessages={() => {
          return fetchMoreMessages();
        }}
        hasMoreData={hasMoreMessages}
        filterMessage={(e) => {
          return updateFilter(e);
        }}
        filterMessageByQuery={(e) => {
          return filterByQuery(e);
        }}
        noResults={showNoResultsLabel}
        updateReadStatus={changeReadStatus}
        markAsArchived={archiveConnection}
        markAsUnarchived={restoreConnection}
      />
      {showNoResultsLabel ? (
        <div className="mt-8 mx-auto">{t('auth.inbox.noMessages')}</div>
      ) : (
        <>
          <MessageSection
            locale={locale}
            loading={conversationLoading || loading}
            toggleProfileDetails={toggleProfileDetails}
            showProfileDetails={showProfileDetails}
            conversation={selectedConversation || ''}
            handleMessage={updateMessage}
            sending={sending}
            recipient={selectedRecipient}
            proposalTitle={selectedProposalTitle}
            avatar={selectedRecipientImage}
            updateReadStatus={updateReadStatus}
            markAsArchived={updateArchiveStatus}
            restoreConnection={updateArchiveStatus}
            isRead={isRead}
            isProposalUser={
              selectedConnection
                ? selectedConnection.viewingByProposalUser
                : false
            }
            canViewMessages={
              selectedConnection
                ? selectedConnection.viewerCanViewMessages
                : false
            }
            canPostMessages={
              selectedConnection
                ? selectedConnection.viewerCanPostMessages
                : false
            }
            connection={selectedConnection || null}
            acceptConnection={connectionAction}
            rejectConnection={connectionAction}
            loadMoreMessages={() => {
              return fetchPreviousMessages();
            }}
            interestedUserDeactivated={
              selectedConnection
                ? selectedConnection.interlocutor.deactivated
                : false
            }
            acceptingNudge={acceptingNudge}
            rejectingNudge={rejectingNudge}
          />
          <UserInfo
            locale={locale}
            showProfileDetails={showProfileDetails}
            conversation={selectedConnection}
            attachments={attachmentCollection}
            acceptConnection={connectionAction}
            rejectConnection={connectionAction}
            isProposalUser={
              selectedConnection
                ? selectedConnection.viewingByProposalUser
                : true
            }
            acceptingNudge={acceptingNudge}
            rejectingNudge={rejectingNudge}
          />
        </>
      )}
    </div>
  );
};

export default Inbox;

Inbox.propTypes = {
  locale: PropTypes.string,
  apiUrl: PropTypes.string,
  connectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  acceptNudge: PropTypes.bool,
  rejectNudge: PropTypes.bool
};
